/**
 * /admin/discovery — 资源发现：从用户 bio / 频道简介 / 群消息里挖出来的
 * 频道与群资源池。
 *
 * Three tabs backed by the discovery admin API:
 *   频道与群  — tg_chats (resolved metadata, discovery count, sources drawer)
 *   用户      — tg_users (bio + link count)
 *   任务队列  — discovery_jobs (pending / failed, retry)
 *
 * Header cards come from /stats and auto-refresh every 15s so the operator
 * can watch the crawl progress + per-account budget / cooldown.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type DiscoveredChatSummary,
  type DiscoveredUserSummary,
  type DiscoveryBackfillResponse,
  type DiscoveryJobSummary,
  type DiscoveryStats,
  type ResourceLinkSummary,
  Routes,
} from '@tg-leads/contracts';

import { api } from '../../lib/api';
import { toastError, toastInfo, toastSuccess } from '../../lib/toast';

type Tab = 'chats' | 'users' | 'jobs';

/** Delay a fast-changing input (search box) before it enters a queryKey. */
function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '' || v === false) continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

const KIND_LABEL: Record<string, string> = {
  channel: '频道',
  supergroup: '超级群',
  group: '群',
  gigagroup: '广播群',
  unknown: '未知',
};

const STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  resolved: { text: '已解析', cls: 'badge-ok' },
  preview: { text: '仅预览', cls: 'badge-warn' },
  pending: { text: '待解析', cls: 'badge-neutral' },
  failed: { text: '失败', cls: 'badge-fail' },
};

const SOURCE_LABEL: Record<ResourceLinkSummary['sourceType'], string> = {
  user_bio: '用户简介',
  chat_description: '频道/群简介',
  message: '群消息',
  web_search: '网页搜索',
};

export function DiscoveryPage() {
  const [tab, setTab] = useState<Tab>('chats');
  const qc = useQueryClient();

  const stats = useQuery({
    queryKey: ['discovery', 'stats'],
    queryFn: () => api.get<DiscoveryStats>(Routes.admin.discoveryStats),
    refetchInterval: 15_000,
  });

  const backfill = useMutation({
    mutationFn: (body: { users?: boolean; members?: boolean; retryFailed?: boolean }) =>
      api.post<DiscoveryBackfillResponse>(Routes.admin.discoveryBackfill, body),
    onSuccess: (r) => {
      const parts: string[] = [];
      if (r.enqueuedUsers) parts.push(`用户 ${r.enqueuedUsers} 个`);
      if (r.enqueuedMemberScans) parts.push(`群成员扫描 ${r.enqueuedMemberScans} 个群`);
      if (r.retriedFailed) parts.push(`重试失败任务 ${r.retriedFailed} 个`);
      if (parts.length) toastSuccess(`已加入队列：${parts.join('，')}`);
      else toastInfo('没有新的任务需要加入');
      qc.invalidateQueries({ queryKey: ['discovery'] });
    },
    onError: (e) => toastError(e),
  });

  const s = stats.data;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">资源发现</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            观察号在群里看到谁，就顺手抓谁的公开简介；简介里提到的频道 / 群再抓它们的简介，层层扩展。
            所有 RPC 走观察号，按"系统配置 → 资源发现"里的每日预算限速，遇到 FLOOD 自动停。这里只收集，不自动加群。
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            className="btn-secondary text-xs"
            disabled={backfill.isPending}
            title="把线索表里的目标用户 + 还没抓过简介的用户全部排进队列"
            onClick={() => backfill.mutate({ users: true })}
          >
            回填历史用户
          </button>
          <button
            className="btn-secondary text-xs"
            disabled={backfill.isPending}
            title="对每个已加入的群拉一遍成员列表（大群会被 TG 截断；观察号被限流时先别点）"
            onClick={() => {
              if (confirm('扫描已加入群的成员列表属于风控敏感操作，确定要排队吗？')) {
                backfill.mutate({ members: true });
              }
            }}
          >
            扫描群成员
          </button>
          <button
            className="btn-secondary text-xs"
            disabled={backfill.isPending || !s || s.jobs.failed === 0}
            onClick={() => backfill.mutate({ retryFailed: true })}
          >
            重试失败任务{s ? `（${s.jobs.failed}）` : ''}
          </button>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            title="频道 / 群"
            main={s.chats.total}
            sub={`已解析 ${s.chats.resolved} · 仅预览 ${s.chats.preview} · 待解析 ${s.chats.pending} · 失败 ${s.chats.failed}`}
            foot={`其中 ${s.chats.monitored} 个已在监听`}
          />
          <StatCard
            title="用户"
            main={s.users.total}
            sub={`已抓简介 ${s.users.bioFetched} · 简介里带链接 ${s.users.withLinks}`}
          />
          <StatCard title="发现关系" main={s.links} sub="谁的简介 / 哪条消息提到了哪个资源" />
          <StatCard
            title={`任务队列${s.enabled ? '' : '（已停用）'}`}
            main={s.jobs.pending}
            sub={`执行中 ${s.jobs.running} · 失败 ${s.jobs.failed} · 今日完成 ${s.jobs.doneToday}`}
            warn={!s.enabled}
          />
        </div>
      )}

      {s && s.accounts.length > 0 && (
        <div className="card">
          <div className="text-sm font-medium mb-2">观察号预算（今日）</div>
          <div className="flex flex-wrap gap-3 text-xs">
            {s.accounts.map((a) => {
              const pct = a.budget > 0 ? Math.min(100, Math.round((a.usedToday / a.budget) * 100)) : 0;
              return (
                <div key={a.accountId} className="border rounded px-3 py-2 min-w-[14rem]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono">{a.phone}</span>
                    <span className={a.connected ? 'badge badge-ok' : 'badge badge-fail'}>
                      {a.connected ? '在线' : '离线'}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 bg-slate-100 rounded overflow-hidden">
                    <div className={`h-full ${pct >= 90 ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-1 text-slate-500">
                    简介 {a.usedToday} / {a.budget} · 解析 {a.usedResolveToday} / {a.resolveBudget}
                    {a.cooldownUntil && (
                      <span className="ml-2 text-orange-600">
                        限流冷却至 {new Date(a.cooldownUntil).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {s && s.accounts.length === 0 && (
        <div className="card text-sm text-amber-700 bg-amber-50 border-amber-200">
          没有可用的观察号（purpose = observer 且 active）。资源发现只走观察号，发送号不参与。
        </div>
      )}

      <div className="flex gap-1 border-b">
        {(
          [
            ['chats', '频道与群'],
            ['users', '用户'],
            ['jobs', '任务队列'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${
              tab === k ? 'border-slate-900 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
            onClick={() => setTab(k)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'chats' && <ChatsTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'jobs' && <JobsTab />}
    </div>
  );
}

function StatCard(props: { title: string; main: number; sub?: string; foot?: string; warn?: boolean }) {
  return (
    <div className={`card ${props.warn ? 'border-amber-300 bg-amber-50' : ''}`}>
      <div className="text-xs text-slate-500">{props.title}</div>
      <div className="text-2xl font-semibold mt-1">{props.main.toLocaleString()}</div>
      {props.sub && <div className="text-[11px] text-slate-500 mt-1">{props.sub}</div>}
      {props.foot && <div className="text-[11px] text-slate-400">{props.foot}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- chats

function ChatsTab() {
  const qc = useQueryClient();
  const [qRaw, setQ] = useState('');
  const q = useDebounced(qRaw);
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [excludeMonitored, setExcludeMonitored] = useState(false);
  const [sort, setSort] = useState<'discoveredCount' | 'membersCount' | 'lastDiscoveredAt'>('discoveredCount');
  const [expanded, setExpanded] = useState<string | null>(null);

  const list = useInfiniteQuery({
    queryKey: ['discovery', 'chats', { q, kind, status, excludeMonitored, sort }],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.get<Page<DiscoveredChatSummary>>(
        `${Routes.admin.discoveryChats}${qs({ q, kind, resolveStatus: status, excludeMonitored, sort, cursor: pageParam, limit: 50 })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });

  const resolveNow = useMutation({
    mutationFn: (id: string) => api.post<{ queued: true }>(Routes.admin.discoveryChatResolve(id), {}),
    onSuccess: () => {
      toastSuccess('已插队，稍后刷新查看');
      qc.invalidateQueries({ queryKey: ['discovery'] });
    },
    onError: (e) => toastError(e),
  });

  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center text-sm">
        <input className="input w-64" placeholder="搜标题 / 用户名 / 简介" value={qRaw} onChange={(e) => setQ(e.target.value)} />
        <select className="input w-32" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">全部类型</option>
          <option value="channel">频道</option>
          <option value="supergroup">超级群</option>
          <option value="group">群</option>
          <option value="unknown">未知</option>
        </select>
        <select className="input w-32" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">全部状态</option>
          <option value="resolved">已解析</option>
          <option value="preview">仅预览</option>
          <option value="pending">待解析</option>
          <option value="failed">失败</option>
        </select>
        <select className="input w-40" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="discoveredCount">按被提到次数</option>
          <option value="membersCount">按成员数</option>
          <option value="lastDiscoveredAt">按最近发现</option>
        </select>
        <label className="flex items-center gap-1 text-slate-600">
          <input type="checkbox" checked={excludeMonitored} onChange={(e) => setExcludeMonitored(e.target.checked)} />
          隐藏已在监听的
        </label>
      </div>

      <div className="card overflow-x-auto">
        {list.isLoading ? (
          <div className="text-sm text-slate-500">加载中…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500">还没有发现任何频道 / 群。观察号上线并收到消息后会自动开始。</div>
        ) : (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 uppercase border-b">
                <th className="py-2 pr-3">名称</th>
                <th className="py-2 pr-3">类型</th>
                <th className="py-2 pr-3">成员</th>
                <th className="py-2 pr-3">被提到</th>
                <th className="py-2 pr-3">层级</th>
                <th className="py-2 pr-3">状态</th>
                <th className="py-2 pr-3">最近发现</th>
                <th className="py-2 pr-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <Fragment key={c.id}>
                  <tr
                    className="border-b last:border-0 cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpanded((cur) => (cur === c.id ? null : c.id))}
                  >
                    <td className="py-2 pr-3 max-w-[22rem]">
                      <div className="font-medium truncate" title={c.title ?? ''}>
                        {c.title ?? <span className="text-slate-400">（未解析）</span>}
                        {c.isMonitored && <span className="badge badge-ok ml-2">已监听</span>}
                      </div>
                      <div className="text-slate-500 font-mono truncate">
                        {c.username ? `@${c.username}` : c.inviteHash ? `+${c.inviteHash}` : c.tgChatId}
                      </div>
                    </td>
                    <td className="py-2 pr-3">{KIND_LABEL[c.kind] ?? c.kind}</td>
                    <td className="py-2 pr-3">{c.membersCount?.toLocaleString() ?? '—'}</td>
                    <td className="py-2 pr-3">{c.discoveredCount}</td>
                    <td className="py-2 pr-3">{c.depth}</td>
                    <td className="py-2 pr-3">
                      <span className={`badge ${STATUS_LABEL[c.resolveStatus]?.cls ?? 'badge-neutral'}`} title={c.resolveError ?? ''}>
                        {STATUS_LABEL[c.resolveStatus]?.text ?? c.resolveStatus}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-slate-500">{new Date(c.lastDiscoveredAt).toLocaleString()}</td>
                    <td className="py-2 pr-3 space-x-2 whitespace-nowrap">
                      {c.link && (
                        <button
                          className="btn-secondary text-xs"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            void navigator.clipboard.writeText(c.link!).then(
                              () => toastSuccess('链接已复制，可粘贴到「已加入的群 → 加入群」'),
                              () => toastInfo(c.link!),
                            );
                          }}
                        >
                          复制链接
                        </button>
                      )}
                      <button
                        className="btn-secondary text-xs"
                        disabled={resolveNow.isPending}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          resolveNow.mutate(c.id);
                        }}
                      >
                        重新解析
                      </button>
                    </td>
                  </tr>
                  {expanded === c.id && (
                    <tr className="bg-slate-50">
                      <td colSpan={8} className="py-2 px-3">
                        <ChatDetail chat={c} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
        {list.hasNextPage && (
          <div className="pt-3">
            <button className="btn-secondary text-xs" disabled={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>
              {list.isFetchingNextPage ? '加载中…' : '加载更多'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ChatDetail({ chat }: { chat: DiscoveredChatSummary }) {
  const sources = useQuery({
    queryKey: ['discovery', 'chat-sources', chat.id],
    queryFn: () => api.get<{ items: ResourceLinkSummary[] }>(Routes.admin.discoveryChatSources(chat.id)),
  });
  return (
    <div className="grid md:grid-cols-2 gap-4 text-xs">
      <div>
        <div className="font-medium mb-1">简介</div>
        <pre className="whitespace-pre-wrap break-words bg-white border rounded p-2 max-h-48 overflow-y-auto">
          {chat.description || '（无简介 / 未解析）'}
        </pre>
        {chat.resolveError && <div className="mt-1 text-red-700">解析错误：{chat.resolveError}</div>}
        <div className="mt-1 text-slate-500">
          TG ID {chat.tgChatId ?? '—'} · 首次发现 {new Date(chat.firstDiscoveredAt).toLocaleString()}
        </div>
      </div>
      <div>
        <div className="font-medium mb-1">谁提到了它</div>
        {sources.isLoading ? (
          <div className="text-slate-500">加载中…</div>
        ) : (sources.data?.items.length ?? 0) === 0 ? (
          <div className="text-slate-500">无记录</div>
        ) : (
          <ul className="space-y-1">
            {sources.data!.items.map((l) => (
              <li key={l.id} className="bg-white border rounded px-2 py-1">
                <span className="badge badge-neutral mr-2">{SOURCE_LABEL[l.sourceType]}</span>
                {l.sourceUser && (
                  <span>
                    {l.sourceUser.firstName ?? ''}{' '}
                    <span className="font-mono text-slate-500">
                      {l.sourceUser.username ? `@${l.sourceUser.username}` : l.sourceUser.tgUserId}
                    </span>
                  </span>
                )}
                {l.sourceChat && (
                  <span>
                    {l.sourceChat.title ?? ''}{' '}
                    <span className="font-mono text-slate-500">{l.sourceChat.username ? `@${l.sourceChat.username}` : ''}</span>
                  </span>
                )}
                {l.sourceNote && <span className="text-slate-500">{l.sourceNote}</span>}
                <span className="ml-2 text-slate-400 font-mono">{l.rawRef}</span>
                <span className="ml-2 text-slate-400">{new Date(l.discoveredAt).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- users

function UsersTab() {
  const [qRaw, setQ] = useState('');
  const q = useDebounced(qRaw);
  const [withLinks, setWithLinks] = useState(false);
  const [unfetched, setUnfetched] = useState(false);

  const list = useInfiniteQuery({
    queryKey: ['discovery', 'users', { q, withLinks, unfetched }],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.get<Page<DiscoveredUserSummary>>(
        `${Routes.admin.discoveryUsers}${qs({ q, withLinks, unfetched, cursor: pageParam, limit: 50 })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-center text-sm">
        <input className="input w-64" placeholder="搜用户名 / 名字 / 简介 / TG ID" value={qRaw} onChange={(e) => setQ(e.target.value)} />
        <label className="flex items-center gap-1 text-slate-600">
          <input type="checkbox" checked={withLinks} onChange={(e) => setWithLinks(e.target.checked)} />
          只看简介里带资源的
        </label>
        <label className="flex items-center gap-1 text-slate-600">
          <input type="checkbox" checked={unfetched} onChange={(e) => setUnfetched(e.target.checked)} />
          只看还没抓简介的
        </label>
      </div>
      <div className="card overflow-x-auto">
        {list.isLoading ? (
          <div className="text-sm text-slate-500">加载中…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500">没有记录。</div>
        ) : (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 uppercase border-b">
                <th className="py-2 pr-3">用户</th>
                <th className="py-2 pr-3">简介</th>
                <th className="py-2 pr-3">资源数</th>
                <th className="py-2 pr-3">来源</th>
                <th className="py-2 pr-3">简介抓取</th>
                <th className="py-2 pr-3">最近出现</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-b last:border-0">
                  <td className="py-2 pr-3">
                    <div className="font-medium">
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                      {u.isBot && <span className="badge badge-neutral ml-1">bot</span>}
                      {u.isPremium && <span className="badge badge-warn ml-1">Premium</span>}
                    </div>
                    <div className="font-mono text-slate-500">{u.username ? `@${u.username}` : u.tgUserId}</div>
                  </td>
                  <td className="py-2 pr-3 max-w-[28rem]">
                    <div className="truncate" title={u.bio ?? ''}>{u.bio || <span className="text-slate-400">{u.bioFetchedAt ? '（空）' : '（未抓取）'}</span>}</div>
                  </td>
                  <td className="py-2 pr-3">{u.linkCount}</td>
                  <td className="py-2 pr-3 text-slate-500">
                    {{ message: '群消息', member_list: '群成员', lead: '线索', link: '被提及' }[u.firstSource] ?? u.firstSource}
                  </td>
                  <td className="py-2 pr-3 text-slate-500">{u.bioFetchedAt ? new Date(u.bioFetchedAt).toLocaleString() : '—'}</td>
                  <td className="py-2 pr-3 text-slate-500">{new Date(u.lastSeenAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {list.hasNextPage && (
          <div className="pt-3">
            <button className="btn-secondary text-xs" disabled={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>
              {list.isFetchingNextPage ? '加载中…' : '加载更多'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- jobs

function JobsTab() {
  const [status, setStatus] = useState<'' | DiscoveryJobSummary['status']>('');
  const [kind, setKind] = useState<'' | DiscoveryJobSummary['kind']>('');
  const list = useInfiniteQuery({
    queryKey: ['discovery', 'jobs', { status, kind }],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.get<Page<DiscoveryJobSummary>>(
        `${Routes.admin.discoveryJobs}${qs({ status, kind, cursor: pageParam, limit: 100 })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // Only the first page is re-polled; the stats header already refreshes.
    refetchInterval: 15_000,
    maxPages: 1,
  });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const KIND: Record<DiscoveryJobSummary['kind'], string> = {
    fetch_user: '抓用户简介',
    resolve_chat: '解析频道/群',
    list_members: '扫描群成员',
  };
  const ST: Record<DiscoveryJobSummary['status'], { text: string; cls: string }> = {
    pending: { text: '排队中', cls: 'badge-neutral' },
    running: { text: '执行中', cls: 'badge-warn' },
    done: { text: '完成', cls: 'badge-ok' },
    failed: { text: '失败', cls: 'badge-fail' },
    expired: { text: '过期', cls: 'badge-neutral' },
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center text-sm">
        <select className="input w-32" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="">全部状态</option>
          <option value="pending">排队中</option>
          <option value="running">执行中</option>
          <option value="failed">失败</option>
          <option value="expired">过期</option>
          <option value="done">完成</option>
        </select>
        <select className="input w-40" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          <option value="">全部类型</option>
          <option value="fetch_user">抓用户简介</option>
          <option value="resolve_chat">解析频道/群</option>
          <option value="list_members">扫描群成员</option>
        </select>
      </div>
      <div className="card overflow-x-auto">
        {list.isLoading ? (
          <div className="text-sm text-slate-500">加载中…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500">队列为空。</div>
        ) : (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 uppercase border-b">
                <th className="py-2 pr-3">类型</th>
                <th className="py-2 pr-3">目标</th>
                <th className="py-2 pr-3">层级</th>
                <th className="py-2 pr-3">优先级</th>
                <th className="py-2 pr-3">状态</th>
                <th className="py-2 pr-3">尝试</th>
                <th className="py-2 pr-3">下次执行</th>
                <th className="py-2 pr-3">错误</th>
              </tr>
            </thead>
            <tbody>
              {items.map((j) => (
                <tr key={j.id} className="border-b last:border-0">
                  <td className="py-2 pr-3">{KIND[j.kind]}</td>
                  <td className="py-2 pr-3 font-mono">{j.ref}</td>
                  <td className="py-2 pr-3">{j.depth}</td>
                  <td className="py-2 pr-3">{j.priority}</td>
                  <td className="py-2 pr-3"><span className={`badge ${ST[j.status].cls}`}>{ST[j.status].text}</span></td>
                  <td className="py-2 pr-3">{j.attempts}</td>
                  <td className="py-2 pr-3 text-slate-500">{j.status === 'pending' ? new Date(j.nextAttemptAt).toLocaleString() : j.doneAt ? new Date(j.doneAt).toLocaleString() : '—'}</td>
                  <td className="py-2 pr-3 max-w-[20rem] truncate text-red-700" title={j.lastError ?? ''}>{j.lastError ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {list.hasNextPage && (
          <div className="pt-3">
            <button className="btn-secondary text-xs" disabled={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>
              {list.isFetchingNextPage ? '加载中…' : '加载更多'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
